import { createSelector } from '@ngrx/store';
import type { Dictionary } from '@ngrx/entity';
import type { DeviceSyncStatus } from '@rocksoft/apple-sync-sdk';
import { DeviceStoreSelectors, LocationSubset, type DeviceHealthState } from '@era/stores';
import type { Device } from '@rocksoft/shared';
import { DeviceSyncStatusSelectors } from '../../sub-features/device-sync/device-sync-statuses-store';
import { isDeviceInCorrectSyncState } from '../../sub-features/device-sync/device-sync-statuses-store/device-sync-status.utils';

// Temporary untill we start using in-app Heartbeat to determine online/offline status. (#41491)
// For now, we will consider a device online if it has been seen in the last 48 hours.
const ONLINE_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Selects devices from the current location subset based on their health state.
 * @param locationSubset - The subset of devices to filter (e.g., All, Assigned, Spare, etc.).
 * @param healthState - The health state to filter devices by (e.g., 'all', 'online', 'offline', 'sync-issues').
 * @param now - The current timestamp.
 * @returns A selector that returns an array of devices matching the specified health state within the given location subset.
 */
export const selectCurrentLocationSubsetDevicesByHealthState = (
  locationSubset: LocationSubset,
  healthState: DeviceHealthState,
  now: number
) =>
  createSelector(
    DeviceStoreSelectors.selectCurrentLocationDevicesForSubset(locationSubset),
    DeviceSyncStatusSelectors.selectDeviceSyncStatusEntities,
    (devices: Device[], syncStatuses) => filterDevicesByHealthState(devices, syncStatuses, healthState, now)
  );

function isDeviceOnline(device: Device, now: number): boolean {
  if (!device.lastSeen) {
    return false;
  }

  const lastSeen = Date.parse(device.lastSeen);
  return Number.isFinite(lastSeen) && lastSeen >= now - ONLINE_WINDOW_MS && lastSeen <= now;
}

function isDeviceOffline(device: Device, now: number): boolean {
  return !isDeviceOnline(device, now);
}

function hasDeviceSyncIssues(device: Device, syncStatuses: Dictionary<DeviceSyncStatus> | undefined): boolean {
  return !isDeviceInCorrectSyncState(syncStatuses?.[device.udid]);
}

function filterDevicesByHealthState(
  devices: readonly Device[],
  syncStatuses: Dictionary<DeviceSyncStatus> | undefined,
  healthState: DeviceHealthState,
  now: number
): Device[] {
  if (healthState === 'all') {
    return [...devices];
  }

  return devices.filter((device) => {
    if (healthState === 'online') {
      return isDeviceOnline(device, now) && !hasDeviceSyncIssues(device, syncStatuses);
    }
    if (healthState === 'offline') {
      return isDeviceOffline(device, now);
    }
    return hasDeviceSyncIssues(device, syncStatuses);
  });
}
